/**
 * Self-hosted voice pipeline — browser audio I/O (framework-agnostic).
 *
 * This module owns the two raw-media halves of the local (non-ElevenLabs) voice
 * spike, with **no React imports** so it can be unit-tested and reused outside a
 * component tree:
 *
 *   1. Capture: getUserMedia → MediaRecorder(Opus) → per-chunk callback. Each
 *      chunk is one container-framed Opus blob the caller wraps as a `[0x00]`
 *      audio frame and ships over the audio WebSocket (see useSelfHostedVoice).
 *   2. Playback: a Web-Audio queue that decodes incoming Opus frames and plays
 *      them gap-free, plus `clearPlayback()` for barge-in (tap-to-interrupt).
 *
 * Half-duplex (push-to-talk): the spike never plays the agent and captures the
 * user at the same time. The caller mutes capture while the floor is the
 * agent's; `setCaptureMuted(true)` keeps the MediaRecorder running (so we don't
 * pay a fresh getUserMedia + recorder-spin-up on every turn) but drops its
 * chunks on the floor, which is cheaper and lower-latency than stop/start.
 *
 * Browser-API caveats (load-bearing — do not "simplify" away):
 *   - MediaRecorder emits **container-framed** Opus (Ogg or WebM pages), not
 *     bare Opus packets. The first `dataavailable` chunk carries the container
 *     header; subsequent chunks are continuation pages that are only decodable
 *     in sequence after that header. The server-side decoder must treat the
 *     stream as a continuous container, not as independently-decodable packets.
 *     We therefore start the recorder ONCE per session and timeslice it, rather
 *     than start/stop per utterance (a stop/start would re-emit a header and
 *     restart the byte stream, which the streaming ASR side would have to
 *     re-sync on).
 *   - mimeType support differs by browser. We prefer `audio/ogg;codecs=opus`
 *     (Firefox, incl. Firefox-Android — see the codec note in CLAUDE.md memory
 *     "Firefox-Android MediaRecorder codec") and fall back to
 *     `audio/webm;codecs=opus` (Chrome/Edge/Chrome-Android). Safari historically
 *     supports neither for MediaRecorder; capture start rejects there.
 *   - Playback uses `decodeAudioData`, which wants a self-contained encoded blob
 *     per call. For the spike the server sends back **whole synthesized
 *     utterances** (one container per agent turn) rather than a raw continuous
 *     Opus stream, so each enqueued frame decodes standalone. If the TTS side is
 *     later changed to stream bare Opus packets, this playback path must move to
 *     a MediaSource/decoder-worklet — flagged here so it isn't a silent break.
 */

/** Tuning knobs for {@link AudioCapturePipeline}. All optional. */
export interface AudioCaptureOptions {
  /**
   * MediaRecorder timeslice in ms — how often `dataavailable` fires. 250ms is a
   * good half-duplex tradeoff: small enough that PTT-release flushes promptly,
   * large enough to keep per-chunk container overhead and WS message rate sane.
   */
  timesliceMs?: number;
  /** getUserMedia audio constraints override (merged over the half-duplex defaults). */
  audioConstraints?: MediaTrackConstraints;
  /** Called once per MediaRecorder chunk with the container-framed Opus bytes. */
  onFrame?: (opus: Uint8Array) => void;
  /** Called when capture stops unexpectedly (track ended, device unplugged). */
  onCaptureEnded?: (reason: string) => void;
  /** Called when playback drains (last queued utterance finished). */
  onPlaybackIdle?: () => void;
}

/**
 * Picks the first MediaRecorder mimeType the browser supports, preferring the
 * Firefox-Android-friendly Ogg/Opus container, then WebM/Opus. Returns null
 * when neither is available (Safari) so the caller can fail with a clear error.
 */
export function pickOpusMimeType(): string | null {
  if (typeof MediaRecorder === 'undefined') return null;
  const candidates = ['audio/ogg;codecs=opus', 'audio/webm;codecs=opus'];
  for (const mime of candidates) {
    try {
      if (MediaRecorder.isTypeSupported(mime)) return mime;
    } catch {
      // isTypeSupported can throw on very old engines — treat as unsupported.
    }
  }
  return null;
}

const DEFAULT_TIMESLICE_MS = 250;

/**
 * AudioCapturePipeline — start/stop mic capture (Opus chunks via callback) and
 * a Web-Audio playback queue for incoming agent audio. One instance per voice
 * session; framework-agnostic.
 */
export class AudioCapturePipeline {
  private readonly opts: AudioCaptureOptions;

  // --- Capture state ---
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private mimeType: string | null = null;
  /** Half-duplex gate: when true, chunks are produced but dropped (mic "muted"). */
  private captureMuted = false;
  private capturing = false;

  // --- Playback state ---
  private audioCtx: AudioContext | null = null;
  /** Monotonic next-start time (in AudioContext clock) for gap-free queueing. */
  private playbackCursor = 0;
  /** Live source nodes so barge-in can stop them immediately. */
  private activeSources = new Set<AudioBufferSourceNode>();
  /** Generation counter — bumped by clearPlayback so late decodes are dropped. */
  private playbackGeneration = 0;
  /** Pending decode count, to fire onPlaybackIdle when the queue truly drains. */
  private pendingPlayback = 0;

  constructor(opts: AudioCaptureOptions = {}) {
    this.opts = opts;
  }

  /** True once {@link start} has begun a capture session. */
  isCapturing(): boolean {
    return this.capturing;
  }

  /** The negotiated container mimeType (null until {@link start} succeeds). */
  getMimeType(): string | null {
    return this.mimeType;
  }

  /**
   * Acquires the mic and starts the MediaRecorder. Idempotent: a second call
   * while already capturing is a no-op. Rejects when the browser has no Opus
   * MediaRecorder support or the user denies the mic permission.
   */
  async start(): Promise<void> {
    if (this.capturing) return;

    if (typeof navigator === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('getUserMedia is not available in this environment');
    }

    const mime = pickOpusMimeType();
    if (!mime) {
      throw new Error(
        'No supported Opus MediaRecorder mimeType (audio/ogg or audio/webm). ' +
          'This browser cannot capture for the self-hosted voice pipeline.',
      );
    }
    this.mimeType = mime;

    // Half-duplex capture: mono, with the browser AEC/NS the spec asks for.
    // An explicit `audio:{…}` object (never `audio:true`) avoids the
    // silent-track captures seen on Firefox-Android (see CLAUDE.md memory).
    const audioConstraints: MediaTrackConstraints = {
      echoCancellation: true,
      noiseSuppression: true,
      channelCount: 1,
      ...(this.opts.audioConstraints ?? {}),
    };

    const stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    this.stream = stream;

    // If the track ends out from under us (device unplugged, OS revoke), the
    // recorder will go dead silently — surface it.
    for (const track of stream.getAudioTracks()) {
      track.onended = () => {
        if (this.capturing) {
          this.opts.onCaptureEnded?.('audio track ended');
        }
      };
    }

    let recorder: MediaRecorder;
    try {
      recorder = new MediaRecorder(stream, { mimeType: mime });
    } catch (err) {
      // Clean up the just-acquired stream before bubbling — otherwise the mic
      // indicator stays lit after a failed start.
      this.releaseStream();
      throw new Error(
        `MediaRecorder construction failed for ${mime}: ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }
    this.recorder = recorder;

    recorder.ondataavailable = (ev: BlobEvent) => {
      // Drop empty pings and anything captured while muted (half-duplex).
      if (this.captureMuted) return;
      if (!ev.data || ev.data.size === 0) return;
      const onFrame = this.opts.onFrame;
      if (!onFrame) return;
      // Blob → ArrayBuffer → Uint8Array. arrayBuffer() is a promise; we must
      // not float it (CLAUDE.md no-floating-promises), so handle errors inline.
      void ev.data
        .arrayBuffer()
        .then((buf) => {
          // A late chunk after stop() is harmless to forward, but guard anyway.
          if (!this.capturing || this.captureMuted) return;
          onFrame(new Uint8Array(buf));
        })
        .catch(() => {
          /* a single dropped chunk is non-fatal for a half-duplex stream */
        });
    };

    recorder.onerror = () => {
      if (this.capturing) {
        this.opts.onCaptureEnded?.('MediaRecorder error');
      }
    };

    // Start ONCE and timeslice — see the container-framing caveat in the file
    // header. Stop/start per utterance would re-emit container headers.
    recorder.start(this.opts.timesliceMs ?? DEFAULT_TIMESLICE_MS);
    this.capturing = true;
    this.captureMuted = false;
  }

  /**
   * Half-duplex mute. Keeps the recorder running but drops its chunks while
   * `muted` is true (used while the floor is the agent's). Cheaper than
   * stop/start and avoids re-emitting container headers.
   */
  setCaptureMuted(muted: boolean): void {
    this.captureMuted = muted;
    // Also gate the underlying track so the OS mic indicator reflects reality
    // and no audio is even encoded while muted.
    if (this.stream) {
      for (const track of this.stream.getAudioTracks()) {
        track.enabled = !muted;
      }
    }
  }

  /** Current half-duplex mute state. */
  isCaptureMuted(): boolean {
    return this.captureMuted;
  }

  /** Stops capture and releases the mic. Idempotent. */
  stop(): void {
    this.capturing = false;
    if (this.recorder) {
      this.recorder.ondataavailable = null;
      this.recorder.onerror = null;
      try {
        if (this.recorder.state !== 'inactive') this.recorder.stop();
      } catch {
        // Already inactive — ignore.
      }
      this.recorder = null;
    }
    this.releaseStream();
  }

  private releaseStream(): void {
    if (this.stream) {
      for (const track of this.stream.getAudioTracks()) {
        track.onended = null;
        track.stop();
      }
      this.stream = null;
    }
  }

  // --- Playback ------------------------------------------------------------

  private ensureAudioContext(): AudioContext {
    if (!this.audioCtx) {
      const Ctor: typeof AudioContext =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctor();
      this.playbackCursor = this.audioCtx.currentTime;
    }
    return this.audioCtx;
  }

  /**
   * Decodes one incoming Opus frame and queues it for gap-free playback. For
   * the spike each frame is a self-contained synthesized utterance (one
   * container per agent turn — see file header). Late decodes that land after a
   * `clearPlayback()` (barge-in) are discarded via the generation check.
   *
   * The mobile-autoplay caveat: an AudioContext created without a user gesture
   * starts `suspended`; we resume() it here. The first agent turn always
   * follows a PTT tap (a gesture), so resume() succeeds in practice.
   */
  async enqueuePlayback(opus: Uint8Array): Promise<void> {
    if (opus.byteLength === 0) return;
    const ctx = this.ensureAudioContext();
    if (ctx.state === 'suspended') {
      try {
        await ctx.resume();
      } catch {
        // Resume can reject without a user gesture; decode/start below will be
        // a no-op silently rather than throw.
      }
    }

    const generation = this.playbackGeneration;
    this.pendingPlayback++;

    // decodeAudioData wants its own ArrayBuffer. Copy out of the (possibly
    // pooled / larger) backing buffer so we hand it an exact-length slice.
    const copy = opus.slice().buffer;
    let buffer: AudioBuffer;
    try {
      buffer = await ctx.decodeAudioData(copy);
    } catch (err) {
      this.pendingPlayback--;
      throw new Error(
        `decodeAudioData failed (frame ${opus.byteLength}B): ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }

    this.pendingPlayback--;

    // Barge-in happened while we were decoding — drop this stale audio.
    if (generation !== this.playbackGeneration) return;

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(ctx.destination);

    // Schedule contiguously: never in the past (a stale cursor after a long
    // gap would start late/overlapping), so clamp to now.
    const startAt = Math.max(this.playbackCursor, ctx.currentTime);
    source.start(startAt);
    this.playbackCursor = startAt + buffer.duration;

    this.activeSources.add(source);
    source.onended = () => {
      this.activeSources.delete(source);
      if (this.activeSources.size === 0 && this.pendingPlayback === 0) {
        this.opts.onPlaybackIdle?.();
      }
    };
  }

  /**
   * Barge-in: stop all playing/queued agent audio immediately and invalidate
   * any in-flight decode. Called on tap-to-interrupt so the user can speak over
   * the agent without hearing the tail.
   */
  clearPlayback(): void {
    this.playbackGeneration++;
    for (const source of this.activeSources) {
      try {
        source.onended = null;
        source.stop();
        source.disconnect();
      } catch {
        // Already stopped — ignore.
      }
    }
    this.activeSources.clear();
    if (this.audioCtx) {
      this.playbackCursor = this.audioCtx.currentTime;
    }
  }

  /** Full teardown: stop capture, clear playback, close the AudioContext. */
  dispose(): void {
    this.stop();
    this.clearPlayback();
    if (this.audioCtx) {
      const ctx = this.audioCtx;
      this.audioCtx = null;
      void ctx.close().catch(() => {
        /* context may already be closed */
      });
    }
  }
}
